import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import { Check, X, Trash, ArrowClockwise } from "@phosphor-icons/react";

const KIND_LABEL = {
  delete_inventory: "Delete Inventory Bag",
  delete_batch: "Delete Batch",
  delete_schedule: "Delete Schedule",
  leave: "Leave Request",
};
const KIND_TONE = {
  delete_inventory: "red",
  delete_batch: "red",
  delete_schedule: "red",
  leave: "orange",
};
const STATUS_TONE = { pending: "yellow", approved: "green", rejected: "zinc" };

export default function Requests() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [kindFilter, setKindFilter] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (kindFilter) params.kind = kindFilter;
    const r = await api.get("/requests", { params });
    setRows(r.data);
  }, [statusFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    const ok = await confirm({
      title: "Approve request?",
      message: `${KIND_LABEL[row.kind]}\n${row.target_summary || ""}\n\nThe change is applied immediately.`,
      tone: "warning",
      confirmLabel: "Approve & Apply",
    });
    if (!ok) return;
    setBusyId(row.id);
    try {
      await api.post(`/requests/${row.id}/approve`);
      toast.success("Request approved and applied");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const cancelMine = async (id) => {
    const ok = await confirm({
      title: "Cancel your request?",
      message: "This pending request will be withdrawn.",
      tone: "info",
      confirmLabel: "Yes, cancel",
      cancelLabel: "Keep",
    });
    if (!ok) return;
    try {
      await api.delete(`/requests/${id}`);
      toast.success("Request cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pendingCount = rows.filter(r => r.status === "pending").length;

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="APPROVALS"
        title={isAdmin ? "Requests Inbox" : "My Requests"}
        subtitle={isAdmin
          ? `${pendingCount} pending · approve or reject to apply changes`
          : `Your delete & leave requests · waiting for admin approval`}
        action={
          <Btn variant="secondary" onClick={load} data-testid="req-refresh">
            <ArrowClockwise size={14} /> Refresh
          </Btn>
        }
      />

      <Card className="!p-0 mb-4">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <span className="kr-label">Status</span>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="req-status-filter" className="!w-auto">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="">All</option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="kr-label">Kind</span>
            <Select value={kindFilter} onChange={e => setKindFilter(e.target.value)} data-testid="req-kind-filter" className="!w-auto">
              <option value="">All</option>
              <option value="delete_inventory">Delete Inventory</option>
              <option value="delete_batch">Delete Batch</option>
              <option value="delete_schedule">Delete Schedule</option>
              <option value="leave">Leave</option>
            </Select>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No requests" hint={isAdmin ? "You'll see delete requests and leave applications here." : "Your submitted requests will show here."} />
          </div>
        ) : (
          <table className="kr-table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Details</th>
                <th>By</th>
                <th>When</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`req-row-${r.id}`}>
                  <td><Badge tone={KIND_TONE[r.kind] || "zinc"}>{KIND_LABEL[r.kind] || r.kind}</Badge></td>
                  <td className="max-w-md">
                    <div className="font-mono text-xs">{r.target_summary}</div>
                    {r.kind === "leave" && (
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Type: <span className="font-bold">{r.leave_type}</span>
                        {r.reason ? ` · Reason: ${r.reason}` : ""}
                      </div>
                    )}
                    {r.reason && r.kind !== "leave" && (
                      <div className="text-[11px] text-zinc-500 mt-0.5">Reason: {r.reason}</div>
                    )}
                    {r.rejection_reason && (
                      <div className="text-[11px] text-red-600 mt-0.5">Rejected: {r.rejection_reason}</div>
                    )}
                    {r.executed && (
                      <div className="text-[11px] text-green-700 mt-0.5">✓ {r.executed}</div>
                    )}
                  </td>
                  <td>
                    <div className="text-sm font-bold">{r.requester_name}</div>
                    <div className="text-[10px] font-mono uppercase text-zinc-500">{r.requester_role}</div>
                  </td>
                  <td className="font-mono text-xs text-zinc-500">
                    {new Date(r.created_at).toLocaleString("en-IN")}
                  </td>
                  <td><Badge tone={STATUS_TONE[r.status] || "zinc"}>{r.status}</Badge></td>
                  <td>
                    <div className="flex justify-end gap-2">
                      {r.status === "pending" && isAdmin && (
                        <>
                          <button
                            data-testid={`req-approve-${r.id}`}
                            disabled={busyId === r.id}
                            onClick={() => approve(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold disabled:opacity-50"
                          >
                            <Check size={12} weight="bold" /> Approve
                          </button>
                          <button
                            data-testid={`req-reject-${r.id}`}
                            onClick={() => setRejectFor(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-200 hover:bg-red-600 hover:text-white text-zinc-700 text-xs font-bold"
                          >
                            <X size={12} weight="bold" /> Reject
                          </button>
                        </>
                      )}
                      {r.status === "pending" && !isAdmin && r.requester_email === user.email && (
                        <button
                          data-testid={`req-cancel-${r.id}`}
                          onClick={() => cancelMine(r.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-zinc-600 hover:text-red-600 text-xs"
                        >
                          <Trash size={12} weight="bold" /> Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {rejectFor && (
        <RejectModal req={rejectFor} onClose={() => setRejectFor(null)} onDone={() => { setRejectFor(null); load(); }} />
      )}
    </div>
  );
}

function RejectModal({ req, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/requests/${req.id}/reject`, { reason });
      toast.success("Request rejected");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Reject Request">
      <form onSubmit={submit} className="space-y-4" data-testid="reject-form">
        <div className="bg-zinc-50 border border-zinc-200 p-3 text-sm font-mono">
          <div>{req.target_summary}</div>
          <div className="text-xs text-zinc-500 mt-1">By {req.requester_name} · {req.requester_role}</div>
        </div>
        <Field label="Reason for rejection (optional)">
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Insufficient justification" data-testid="reject-reason" />
        </Field>
        <div className="flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="reject-submit">{busy ? "Rejecting…" : "Reject Request"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- Shared helpers used by other pages ----------------
export function useRequestDelete() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const isAdmin = user?.role === "admin";
  return async ({ kind, id, label, directDeleteUrl, onDone }) => {
    if (isAdmin) {
      const ok = await confirm({
        title: "Delete this item?",
        message: label,
        tone: "danger",
      });
      if (!ok) return false;
      try {
        await api.delete(directDeleteUrl);
        toast.success("Deleted");
        onDone?.();
        return true;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed");
        return false;
      }
    } else {
      const reason = window.prompt(`Request deletion for: ${label}\n\nEnter a reason (optional):`, "");
      if (reason === null) return false;
      try {
        await api.post("/requests/delete", { kind, target_id: id, reason });
        toast.success("Delete request sent to admin");
        onDone?.();
        return true;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed");
        return false;
      }
    }
  };
}
